import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

export default function AtlasGettingStarted() {
  return (
    <>
      <NextSeo
        title="Getting started with Atlas | Chat.co"
        description="Check Atlas access, open the workspace and return to Classic."
      />
      <DocsArticleLayout
        category="Atlas"
        categoryColor="text-blue-700"
        title="Getting started with Atlas"
        description="Start with the account, bot and outcome you intend to work on."
        readingTime="4 min read"
        tableOfContents={[
          { id: 'access', title: 'Check your access' },
          { id: 'request', title: 'Make a clear request' },
          { id: 'classic', title: 'Return to Classic' },
        ]}
        prevArticle={{ title: 'Atlas owner guide', href: PageLink.docsAtlas }}
        nextArticle={{ title: 'Review and Apply in Atlas', href: PageLink.docsAtlasApprovals }}>
        <section id="access">
          <h2>Check your access</h2>
          <p>
            Sign in to your own Chat.co account. Atlas is available to enrolled accounts with the
            required permissions. When your account has access, open the{' '}
            <Link href="/home">Atlas workspace</Link>. If that page is unavailable, use your usual
            Chat.co pages and ask the account owner or support to check access.
          </p>
          <DocsCallout title="Enrollment is not enablement">
            Access to Atlas does not enable every workflow or integration. Use only capabilities
            offered in your workspace; onboarding, refresh and recurring routines may be unavailable
            even when Atlas opens.
          </DocsCallout>
          <p>
            The account owner can work across their account once Atlas access is available. Admin
            access depends on the Use Atlas permission, and account-level actions need the relevant
            admin permissions. Team members need Atlas assistant access for assigned bots and remain
            limited to their bot permissions. See{' '}
            <Link href={PageLink.docsAtlasPrivacy}>roles and permissions</Link> before delegating
            work.
          </p>
        </section>
        <section id="request">
          <h2>Make a clear request</h2>
          <ol className="list-decimal space-y-2 pl-6">
            <li>
              Name the bot and your goal. For example, ask to inspect its knowledge sources before
              proposing changes.
            </li>
            <li>
              Read the reply and source evidence. If the target is unclear, clarify it before
              continuing.
            </li>
            <li>
              Open the relevant task to follow its status. Waiting for you means a decision is
              needed; it does not mean the task is complete.
            </li>
            <li>
              For a proposed change, read its own review card. Check the target, draft, scope and
              cost information before clicking its approval button: Apply on hosted workflow
              reviews, or Approve and its send, publish or delete variants on standard reviews.
            </li>
          </ol>
          <p>
            An ordinary chat yes is not approval. Learn how{' '}
            <Link href={PageLink.docsAtlasApprovals}>review and Apply</Link> work before authorizing
            an effect. Do not paste credentials into a request or upload them as knowledge.
          </p>
        </section>
        <section id="classic">
          <h2>Return to Classic</h2>
          <p>
            Select <strong>Classic</strong> in the Atlas header to open the{' '}
            <Link href={PageLink.classicDashboard}>chatbot dashboard</Link>; team members go on to
            an assigned bot. Classic opens for this visit only and does not change where you land:
            the next time you sign in, or select the chat.co logo, you return to Atlas.
          </p>
          <p>
            To come back to Classic later, select Classic again or bookmark the classic dashboard.
            If Classic does not open, try again or contact support.
          </p>
          <p>
            Switching views is not a task cancellation or a reversal of an applied change. Check any
            active task and outstanding review when you return. If its result is unclear, use the{' '}
            <Link href={PageLink.docsAtlasTroubleshooting}>interruption guidance</Link>.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
}
